/**
 * Deep links into the gvadmin desktop app.
 *
 * gvadmin addresses a record as `gvadmin://goto/GV<Entity>/<id>`, where
 * `<Entity>` is the Laravel model's basename with the "Live" prefix removed —
 * a LiveTrack here is a GVTrack over there.
 *
 * The id column in the tables has linked out this way for a long time, and the
 * inspector's ID label now goes to the same place, so the URL shape lives here
 * instead of being spelled out in each component.
 */

/**
 * Models the Admin app has no screen for. A link to one opens nothing, so they
 * get no link. This is the frontend half of the backend's
 * `NotOpenableInAdminApp` marker, which keeps the exported spreadsheet from
 * linking them either.
 *
 * The hardware `Player` (shown as "Unit") is one. gvadmin has a GVPlayer
 * class, but it only shows a unit inside a Location Zone Player's inspector,
 * never on a screen of its own, so `gvadmin://goto/GVPlayer/31` does nothing.
 */
const MODELS_WITHOUT_ADMIN_APP_SCREEN = new Set(['Player']);

/**
 * `App\Models\LiveTrack` (or plain `LiveTrack`) → `Track`. A model the Admin
 * app cannot open gives an empty string, which every caller treats as "no link".
 */
export function adminAppEntityFor(modelName: string): string {
    const segments = modelName.split('\\');
    const modelBasename = segments[segments.length - 1].trim();
    if (MODELS_WITHOUT_ADMIN_APP_SCREEN.has(modelBasename)) {
        return '';
    }
    return modelBasename.replace('Live', '').trim();
}

/** The `gvadmin://` URL for one record of an already-resolved admin entity. */
export function adminAppRecordUrl(adminAppEntity: string, recordId: number | string): string {
    return `gvadmin://goto/GV${adminAppEntity}/${recordId}`;
}
